import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { formatCurrencyInput, parseCurrencyInput } from "@/utils/currencyFormatter";
import { toast } from "sonner";

interface PartialPaymentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: {
    id: string;
    description: string;
    person?: string;
    valorOriginal: number;
    valorPago: number;
  };
  onSave: (payment: {
    transactionId: string;
    value: number;
    date: string;
    paymentMethod: string;
  }) => void;
  suggestedValue?: number;
}

export default function PartialPaymentForm({ 
  open, 
  onOpenChange, 
  transaction,
  onSave,
  suggestedValue
}: PartialPaymentFormProps) {
  const [date, setDate] = useState<Date>(new Date());
  const [value, setValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const saldoDevedor = transaction.valorOriginal - transaction.valorPago;

  // Pre-fill value when modal opens with suggestedValue
  useEffect(() => {
    if (open && suggestedValue && suggestedValue > 0) {
      setValue(formatCurrencyInput(suggestedValue.toFixed(2).replace('.', ',')));
    }
  }, [open, suggestedValue]);

  const handleSave = () => {
    const paymentValue = parseCurrencyInput(value);
    
    if (!paymentValue || paymentValue <= 0) {
      toast.error("Informe um valor válido");
      return;
    }

    if (paymentValue > saldoDevedor) {
      toast.error(`O valor não pode ser maior que o saldo devedor (R$ ${saldoDevedor.toFixed(2)})`);
      return;
    }

    if (!paymentMethod) {
      toast.error("Selecione a forma de pagamento");
      return;
    }

    onSave({
      transactionId: transaction.id,
      value: paymentValue,
      date: format(date, "dd/MM/yyyy"),
      paymentMethod
    });

    setValue("");
    setPaymentMethod("");
    onOpenChange(false);
  };

  const handleCurrencyChange = (newValue: string) => {
    const formatted = formatCurrencyInput(newValue);
    setValue(formatted);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar Pagamento Parcial</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cliente:</span>
              <span className="font-medium">{transaction.person}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Descrição:</span>
              <span className="font-medium">{transaction.description}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Valor Original:</span>
              <span className="font-medium">R$ {transaction.valorOriginal.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Valor Pago:</span>
              <span className="font-medium text-success">R$ {transaction.valorPago.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground font-semibold">Saldo Devedor:</span>
              <span className="font-bold text-destructive">R$ {saldoDevedor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="value">Valor do Pagamento</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                R$
              </span>
              <Input
                id="value"
                type="text"
                inputMode="numeric"
                placeholder="0,00"
                value={value}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Máximo: R$ {saldoDevedor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Data do Pagamento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP", { locale: ptBR }) : "Selecione"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => newDate && setDate(newDate)}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentMethod">Forma de Pagamento</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PIX">PIX</SelectItem>
                <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                <SelectItem value="Cartão de Crédito">Cartão de Crédito</SelectItem>
                <SelectItem value="Cartão de Débito">Cartão de Débito</SelectItem>
                <SelectItem value="Transferência">Transferência</SelectItem>
                <SelectItem value="Boleto">Boleto</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={handleSave}
            >
              Registrar Pagamento
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
